/**
 * ModuleBoxDrawer Component
 * Interactive rectangle drawer for creating module segment boundaries
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 941-1082, 142 lines)
 *
 * Features:
 * - Click and drag to create rectangle
 * - Supports rotation (creates rectangle in rotated coordinate space)
 * - Live preview while dragging
 * - Bounded to polygon (can only start inside polygon)
 * - Yellow dashed preview rectangle
 */

import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { Polygon } from 'react-leaflet';

export interface ModuleBoxDrawerProps {
  /** Whether drawing mode is active */
  isDrawing: boolean;

  /** Polygon bounds to constrain drawing (must click inside this polygon) */
  polygonBounds: [number, number][];

  /** Callback when rectangle creation is complete */
  onBoxCreated: (rect: [number, number][]) => void;

  /** Callback for live updates while dragging (for preview) */
  onDragUpdate: (rect: [number, number][] | null) => void;

  /** Rotation angle in degrees for creating rotated rectangles */
  rotation?: number;
}

// Helper to check if point is inside polygon
function isPointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
  const [lat, lng] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [latI, lngI] = polygon[i];
    const [latJ, lngJ] = polygon[j];

    const intersect =
      lngI > lng !== lngJ > lng && lat < ((latJ - latI) * (lng - lngI)) / (lngJ - lngI) + latI;

    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * ModuleBoxDrawer component for creating rectangular module segment boundaries
 *
 * Creates axis-aligned rectangles in rotated coordinate space, allowing for
 * rectangles that appear rotated in map space.
 *
 * Interaction:
 * 1. Click inside polygon to start
 * 2. Drag to define rectangle
 * 3. Release to create
 * 4. Live preview shown while dragging (yellow dashed)
 */
export function ModuleBoxDrawer({
  isDrawing,
  polygonBounds,
  onBoxCreated,
  onDragUpdate,
  rotation = 0,
}: ModuleBoxDrawerProps) {
  const map = useMap();
  const [isDragging, setIsDragging] = useState(false);
  const [startPoint, setStartPoint] = useState<[number, number] | null>(null);
  const [currentRect, setCurrentRect] = useState<[number, number][] | null>(null);

  // Helper to transform a point from lat/lng to rotated coordinate space
  const transformToRotated = (lat: number, lng: number, centerLat: number, centerLng: number): [number, number] => {
    const radians = -(rotation * Math.PI) / 180; // Negative for inverse rotation
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    const deltaLat = lat - centerLat;
    const deltaLng = lng - centerLng;

    return [
      deltaLat * cos - deltaLng * sin,
      deltaLat * sin + deltaLng * cos
    ];
  };

  // Helper to transform from rotated space back to lat/lng
  const transformFromRotated = (rotLat: number, rotLng: number, centerLat: number, centerLng: number): [number, number] => {
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    return [
      centerLat + (rotLat * cos - rotLng * sin),
      centerLng + (rotLat * sin + rotLng * cos)
    ];
  };

  useEffect(() => {
    if (!isDrawing) return;

    const handleMouseDown = (e: any) => {
      const point: [number, number] = [e.latlng.lat, e.latlng.lng];
      if (isPointInPolygon(point, polygonBounds)) {
        setIsDragging(true);
        setStartPoint(point);
        map.dragging.disable();
      }
    };

    const handleMouseMove = (e: any) => {
      if (!isDragging || !startPoint) return;
      const currentPoint: [number, number] = [e.latlng.lat, e.latlng.lng];

      // Calculate center point
      const centerLat = (startPoint[0] + currentPoint[0]) / 2;
      const centerLng = (startPoint[1] + currentPoint[1]) / 2;

      // Transform start and current points to rotated space
      const [startRotLat, startRotLng] = transformToRotated(startPoint[0], startPoint[1], centerLat, centerLng);
      const [currRotLat, currRotLng] = transformToRotated(currentPoint[0], currentPoint[1], centerLat, centerLng);

      // Create axis-aligned rectangle in rotated space
      const minRotLat = Math.min(startRotLat, currRotLat);
      const maxRotLat = Math.max(startRotLat, currRotLat);
      const minRotLng = Math.min(startRotLng, currRotLng);
      const maxRotLng = Math.max(startRotLng, currRotLng);

      // Transform corners back to lat/lng (4 corners only - no closing point)
      const rect: [number, number][] = [
        transformFromRotated(maxRotLat, minRotLng, centerLat, centerLng), // top-left
        transformFromRotated(maxRotLat, maxRotLng, centerLat, centerLng), // top-right
        transformFromRotated(minRotLat, maxRotLng, centerLat, centerLng), // bottom-right
        transformFromRotated(minRotLat, minRotLng, centerLat, centerLng), // bottom-left
      ];

      setCurrentRect(rect);
      onDragUpdate(rect);
    };

    const handleMouseUp = (e: any) => {
      if (!isDragging || !startPoint) return;

      const endPoint: [number, number] = [e.latlng.lat, e.latlng.lng];

      // Check if rectangle has meaningful size (not just a click)
      const latDiff = Math.abs(startPoint[0] - endPoint[0]);
      const lngDiff = Math.abs(startPoint[1] - endPoint[1]);
      if (latDiff > 0.00001 && lngDiff > 0.00001) {
        // Rectangle is already created in handleMouseMove
        if (currentRect) {
          onBoxCreated(currentRect);
        }
      }

      setIsDragging(false);
      setStartPoint(null);
      setCurrentRect(null);
      onDragUpdate(null);
      // Note: Don't enable map.dragging here - let SpacebarPanController manage it
    };

    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);
    map.getContainer().style.cursor = 'crosshair';

    return () => {
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      map.getContainer().style.cursor = '';
      // Note: Don't enable map.dragging here - let SpacebarPanController manage it
    };
  }, [isDrawing, isDragging, startPoint, map, polygonBounds, onBoxCreated, onDragUpdate, rotation]);

  // Render preview rectangle while dragging
  if (currentRect) {
    return (
      <Polygon
        positions={currentRect}
        pathOptions={{
          color: '#fbbf24',
          fillColor: '#fde047',
          fillOpacity: 0.3,
          weight: 2,
          dashArray: '5, 5',
        }}
      />
    );
  }

  return null;
}
